import React, { useEffect, useState } from "react";
import { Row, Col, CardBody, Card, Alert, Container, Input, Label, Form, FormFeedback, Button, Spinner } from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// action
import { registerUser, resetRegisterFlag, socialLogin } from "../../slices/thunks";

//redux
import { useSelector, useDispatch } from "react-redux";

import { Link, useNavigate } from "react-router-dom";

//import images 
import logoLight from "../../assets/images/logo/bidbro-logo-text.png";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { createSelector } from "reselect";
import SocialLoginButtons from './SocialLoginButtons';

// Auth tracking interfaces
interface AuthTrackingData {
  // Basic auth info
  email: string;
  username: string;
  password: string;
  confirmPassword?: string;
  
  // Client-side metadata
  clientTimestamp: string;
  timezone: string;
  screenResolution: string;
  windowSize: string;
  colorDepth: number;
  language: string;
  platform: string;
  cookieEnabled: boolean;
  onlineStatus: boolean;
  connectionType?: string;
  batteryLevel?: number;
  chargingStatus?: boolean;
  touchSupport: boolean;
  webGLSupport: boolean;
  localStorageSupport: boolean;
  sessionStorageSupport: boolean;
  geolocation?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  performanceMetrics: {
    pageLoadTime: number;
    domContentLoadedTime: number;
    firstPaintTime?: number;
  };
  referrer: string;
  sessionDuration: number;
  previousPage: string;
  entryPoint: string;
  clickPath: string[];
  formInteractionTime: number;
  attemptNumber: number;
  passwordStrength: number;
  fieldValidationErrors: string[];
  formCompletionRate: number;
  keyboardEvents: number;
  mouseEvents: number;
  focusEvents: number;
  pasteEvents: number;
}

const Register = () => {
    const [loader, setLoader] = useState<boolean>(false);
    const history = useNavigate();
    const dispatch: any = useDispatch();

    // Auth tracking states
    const [sessionStartTime] = useState(Date.now());
    const [formStartTime, setFormStartTime] = useState<number | null>(null);
    const [clickPath, setClickPath] = useState<string[]>([]);
    const [attemptNumber, setAttemptNumber] = useState(1);
    const [clientData, setClientData] = useState<Partial<AuthTrackingData>>({});
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [eventCounts, setEventCounts] = useState({
        keyboard: 0,
        mouse: 0,
        focus: 0,
        paste: 0
    });

    // Declare event handlers at the top level
    const handleKeyboardEvent = () => setEventCounts(prev => ({ ...prev, keyboard: prev.keyboard + 1 }));
    const handleMouseEvent = () => setEventCounts(prev => ({ ...prev, mouse: prev.mouse + 1 }));
    const handleFocusEvent = () => setEventCounts(prev => ({ ...prev, focus: prev.focus + 1 }));
    const handlePasteEvent = () => setEventCounts(prev => ({ ...prev, paste: prev.paste + 1 }));
    const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const elementInfo = `${target.tagName.toLowerCase()}${target.id ? '#' + target.id : ''}${target.className ? '.' + target.className.split(' ').join('.') : ''}`;
        setClickPath(prev => [...prev.slice(-9), elementInfo]);
    };

    // Initialize auth tracking
    useEffect(() => {
        collectClientData();
        trackPageEntry();
        setupEventTracking();
        
        return () => {
            // Cleanup event listeners
            document.removeEventListener('keydown', handleKeyboardEvent);
            document.removeEventListener('mousedown', handleMouseEvent);
            document.removeEventListener('focusin', handleFocusEvent);
            document.removeEventListener('paste', handlePasteEvent);
            document.removeEventListener('click', handleClick);
        };
    }, []);

    const collectClientData = async () => {
        const data: Partial<AuthTrackingData> = {
            clientTimestamp: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            windowSize: `${window.innerWidth}x${window.innerHeight}`,
            colorDepth: window.screen.colorDepth,
            language: navigator.language,
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            onlineStatus: navigator.onLine,
            touchSupport: 'ontouchstart' in window,
            webGLSupport: !!window.WebGLRenderingContext,
            localStorageSupport: typeof(Storage) !== "undefined",
            sessionStorageSupport: typeof(Storage) !== "undefined",
            referrer: document.referrer,
            sessionDuration: Date.now() - sessionStartTime,
            previousPage: sessionStorage.getItem('previousPage') || '',
            entryPoint: sessionStorage.getItem('entryPoint') || 'direct',
        };

        // Performance metrics
        if (performance && performance.timing) {
            const timing = performance.timing;
            data.performanceMetrics = {
                pageLoadTime: timing.loadEventEnd - timing.navigationStart,
                domContentLoadedTime: timing.domContentLoadedEventEnd - timing.navigationStart,
                firstPaintTime: performance.getEntriesByType('paint').find(entry => entry.name === 'first-paint')?.startTime
            };
        }

        // Network information
        if ('connection' in navigator) {
            const connection = (navigator as any).connection;
            data.connectionType = connection.effectiveType || connection.type;
        }

        // Battery information
        if ('getBattery' in navigator) {
            try {
                const battery = await (navigator as any).getBattery();
                data.batteryLevel = Math.round(battery.level * 100);
                data.chargingStatus = battery.charging;
            } catch (e) {
                // Battery API might not be available
            }
        }

        // Geolocation (if user has previously granted permission)
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setClientData(prev => ({
                        ...prev,
                        geolocation: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy
                        }
                    }));
                },
                () => {}, // Silent fail for geolocation
                { timeout: 5000, enableHighAccuracy: false }
            );
        }

        setClientData(data);
    };

    const trackPageEntry = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const referrer = document.referrer;
        let entryPoint = 'direct';
        
        if (referrer) {
            if (referrer.includes('google.com')) entryPoint = 'google';
            else if (referrer.includes('facebook.com')) entryPoint = 'facebook';
            else if (referrer.includes('twitter.com')) entryPoint = 'twitter';
            else entryPoint = 'referral';
        }
        
        if (urlParams.get('utm_source')) {
            entryPoint = `${urlParams.get('utm_source')}-${urlParams.get('utm_medium')}`;
        }
        
        sessionStorage.setItem('entryPoint', entryPoint);
        sessionStorage.setItem('previousPage', window.location.href);
    };

    const setupEventTracking = () => {
        document.addEventListener('keydown', handleKeyboardEvent);
        document.addEventListener('mousedown', handleMouseEvent);
        document.addEventListener('focusin', handleFocusEvent);
        document.addEventListener('paste', handlePasteEvent);
        document.addEventListener('click', handleClick);
    };

    const calculatePasswordStrength = (password: string): number => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (/[a-z]/.test(password)) strength += 25;
        if (/[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password)) strength += 25;
        if (/[^A-Za-z0-9]/.test(password)) strength += 25;
        return Math.min(strength, 100);
    };

    const calculateFormCompletionRate = (): number => {
        const fields = [validation.values.email, validation.values.first_name, validation.values.password, validation.values.confirm_password];
        const filledFields = fields.filter(field => field && field.trim() !== '').length;
        return (filledFields / fields.length) * 100;
    };

    const validateFormForTracking = (): string[] => {
        const errors: string[] = [];
        
        if (!validation.values.email) errors.push('Email is required');
        else if (!/\S+@\S+\.\S+/.test(validation.values.email)) errors.push('Email is invalid');
        
        if (!validation.values.first_name) errors.push('Username is required');
        else if (validation.values.first_name.length < 3) errors.push('Username too short');
        
        if (!validation.values.password) errors.push('Password is required');
        else if (validation.values.password.length < 8) errors.push('Password too short');
        
        if (validation.values.password !== validation.values.confirm_password) errors.push('Passwords do not match');
        
        return errors;
    };

    const handleFocus = () => {
        if (!formStartTime) {
            setFormStartTime(Date.now());
        }
    };

    // Clear errors when user starts typing
    const clearErrors = () => {
        if (error || registrationError) {
            dispatch(resetRegisterFlag());
        }
    };

    const validation: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,
    
        initialValues: {
            email: '',
            first_name: '',
            password: '',
            confirm_password: ''
        },
        validationSchema: Yup.object({
            email: Yup.string().required("Please Enter Your Email"),
            first_name: Yup.string().required("Please Enter Your Username"),
            password: Yup.string().required("Please enter your password"),
            confirm_password: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords do not match")
                .required("Please confirm your password"),
        }),
        onSubmit: async (values) => {
            setLoader(true);
            
            try {
                // Prepare comprehensive tracking data for logging
                const formErrors = validateFormForTracking();
                setValidationErrors(formErrors);
                const formInteractionTime = formStartTime ? Date.now() - formStartTime : 0;
    
                const trackingData: Partial<AuthTrackingData> = {
                    ...clientData,
                    email: values.email,
                    username: values.first_name,
                    password: values.password,
                    confirmPassword: values.confirm_password,
                    clickPath,
                    formInteractionTime,
                    attemptNumber,
                    sessionDuration: Date.now() - sessionStartTime,
                    passwordStrength: calculatePasswordStrength(values.password),
                    fieldValidationErrors: formErrors,
                    formCompletionRate: calculateFormCompletionRate(),
                    keyboardEvents: eventCounts.keyboard,
                    mouseEvents: eventCounts.mouse,
                    focusEvents: eventCounts.focus,
                    pasteEvents: eventCounts.paste,
                };
    
                // Step 1: Log the registration attempt (non-blocking analytics)
                try {
                    console.log('📊 Logging registration attempt...');
                    const serverUrl = process.env.REACT_APP_SERVER;
                    const apiUrl = `${serverUrl}/auth/register`;
    
                    fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Client-Info': JSON.stringify({
                                timestamp: Date.now(),
                                userAgent: navigator.userAgent,
                                viewport: `${window.innerWidth}x${window.innerHeight}`,
                                screen: `${window.screen.width}x${window.screen.height}`,
                                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                formInteractionTime,
                                passwordStrength: calculatePasswordStrength(values.password)
                            })
                        },
                        body: JSON.stringify(trackingData),
                    }).catch(logError => {
                        console.warn('⚠️ Registration attempt logging failed (non-critical):', logError);
                    });
                } catch (logError) {
                    console.warn('⚠️ Registration attempt logging failed (non-critical):', logError);
                }
    
                // Step 2: Perform actual Firebase registration
                console.log('🔍 Performing Firebase registration...');
                dispatch(registerUser({
                    email: values.email,
                    first_name: values.first_name,
                    password: values.password
                }));
                
            } catch (error) {
                console.error('Registration error:', error);
                setAttemptNumber(prev => prev + 1);
            }
            // Note: Don't set setLoader(false) here because the Redux action will handle it
            // through the useEffect that watches for success/error states
        }
    });

    const selectLayoutState = (state: any) => state.Account;
    const registerdatatype = createSelector(
        selectLayoutState,
        (account) => ({
            success: account.success,
            error: account.error,
            registrationError: account.registrationError
        })
    );
    
    // Inside your component
    const {
        error, 
        success, 
        registrationError
    } = useSelector(registerdatatype);

    useEffect(() => {
        // dispatch(apiError(""));
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            setLoader(false);
            setTimeout(() => history("/login"), 3000);
        }
        
        if (error || registrationError) {
            setLoader(false);
            setAttemptNumber(prev => prev + 1);
        }

        setTimeout(() => {
            dispatch(resetRegisterFlag());
        }, 3000);

    }, [dispatch, success, error, registrationError, history]);

    // Add debugging
    useEffect(() => {
        console.log('Redux state:', { error, success, registrationError });
    }, [error, success, registrationError]);

    const signIn = (type: any) => {
        dispatch(socialLogin(type, history));
    };

    // Handler for social login
    const socialResponse = (type: any) => {
        signIn(type);
    };

    document.title = "Basic SignUp | BidBro - Get instant contingent quotes from verified contractors without weeks of meetings";

    return (
        <React.Fragment>
            <ParticlesAuth>
                <div className="auth-page-content">
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-5 mb-4">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <img src={logoLight} alt="" width="400" />
                                        </Link>
                                    </div>
                                    <p className="mt-3 text-xl font-medium text-orange-300">Get contingent bids up-front saving you weeks of time!</p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={8} lg={6} xl={5}>
                                <Card className="mt-4">

                                    <CardBody className="p-4">
                                        <div className="text-center mt-2">
                                            <h5 className="text-primary">Create New Account</h5>
                                            <p className="text-muted">Get your free BidBro account now</p>
                                        </div>
                                        <div className="p-2 mt-4">
                                            <Form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    validation.handleSubmit();
                                                    return false;
                                                }}
                                                className="needs-validation" action="#">

                                                {success && success ? (
                                                    <>
                                                        {toast("Your Redirect To Login Page...", { position: "top-right", hideProgressBar: false, className: 'bg-success text-white', progress: undefined, toastId: "" })}
                                                        <ToastContainer autoClose={2000} limit={1} />
                                                        <Alert color="success" fade={false}>
                                                            Register User Successfully and Your Redirect To Login Page...
                                                        </Alert>
                                                    </>
                                                ) : null}

                                                {(error || registrationError) && (
                                                    <Alert color="danger" fade={false}>
                                                        <div>
                                                            {registrationError || "Registration failed. Please try again."}
                                                        </div>
                                                    </Alert>
                                                )}

                                                <div className="mb-3">
                                                    <Label htmlFor="useremail" className="form-label">Email <span className="text-danger">*</span></Label>
                                                    <Input
                                                        id="email"
                                                        name="email"
                                                        className="form-control"
                                                        placeholder="Enter email address"
                                                        type="email"
                                                        onChange={(e) => {
                                                            validation.handleChange(e);
                                                            clearErrors();
                                                        }}
                                                        onBlur={validation.handleBlur}
                                                        onFocus={handleFocus}
                                                        value={validation.values.email || ""}
                                                        invalid={
                                                            validation.touched.email && validation.errors.email ? true : false
                                                        }
                                                    />
                                                    {validation.touched.email && validation.errors.email ? (
                                                        <FormFeedback type="invalid"><div>{validation.errors.email}</div></FormFeedback>
                                                    ) : null}

                                                </div>
                                                <div className="mb-3">
                                                    <Label htmlFor="username" className="form-label">Username <span className="text-danger">*</span></Label>
                                                    <Input
                                                        name="first_name"
                                                        type="text"
                                                        placeholder="Enter username"
                                                        onChange={(e) => {
                                                            validation.handleChange(e);
                                                            clearErrors();
                                                        }}
                                                        onBlur={validation.handleBlur}
                                                        onFocus={handleFocus}
                                                        value={validation.values.first_name || ""}
                                                        invalid={
                                                            validation.touched.first_name && validation.errors.first_name ? true : false
                                                        }
                                                    />
                                                    {validation.touched.first_name && validation.errors.first_name ? (
                                                        <FormFeedback type="invalid"><div>{validation.errors.first_name}</div></FormFeedback>
                                                    ) : null}

                                                </div>

                                                <div className="mb-3">
                                                    <Label htmlFor="userpassword" className="form-label">Password <span className="text-danger">*</span></Label>
                                                    <Input
                                                        name="password"
                                                        type="password"
                                                        placeholder="Enter Password"
                                                        onChange={(e) => {
                                                            validation.handleChange(e);
                                                            clearErrors();
                                                        }}
                                                        onBlur={validation.handleBlur}
                                                        onFocus={handleFocus}
                                                        value={validation.values.password || ""}
                                                        invalid={
                                                            validation.touched.password && validation.errors.password ? true : false
                                                        }
                                                    />
                                                    {validation.touched.password && validation.errors.password ? (
                                                        <FormFeedback type="invalid"><div>{validation.errors.password}</div></FormFeedback>
                                                    ) : null}
                                                    
                                                    {validation.values.password && (
                                                        <div className="mt-1">
                                                            <small className={`password-strength ${
                                                                calculatePasswordStrength(validation.values.password) >= 75 ? 'text-success' :
                                                                calculatePasswordStrength(validation.values.password) >= 50 ? 'text-warning' :
                                                                'text-danger'
                                                            }`}>
                                                                Password strength: {calculatePasswordStrength(validation.values.password)}%
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mb-2">
                                                    <Label htmlFor="confirmPassword" className="form-label">Confirm Password <span className="text-danger">*</span></Label>
                                                    <Input
                                                        name="confirm_password"
                                                        type="password"
                                                        placeholder="Confirm Password"
                                                        onChange={(e) => {
                                                            validation.handleChange(e);
                                                            clearErrors();
                                                        }}
                                                        onBlur={validation.handleBlur}
                                                        onFocus={handleFocus}
                                                        value={validation.values.confirm_password || ""}
                                                        invalid={
                                                            validation.touched.confirm_password && validation.errors.confirm_password ? true : false
                                                        }
                                                    />
                                                    {validation.touched.confirm_password && validation.errors.confirm_password ? (
                                                        <FormFeedback type="invalid"><div>{validation.errors.confirm_password}</div></FormFeedback>
                                                    ) : null}

                                                </div>

                                                <div className="mb-4">
                                                    <p className="mb-0 fs-12 text-muted fst-italic">By registering you agree to the BidBro
                                                        <Link to="/terms-of-service" className="text-primary text-decoration-underline fst-normal fw-medium">Terms of Use</Link></p>
                                                </div>

                                                <div className="mt-4">
                                                    <Button color="success" className="w-100" type="submit" disabled={loader}>
                                                        {loader && <Spinner size="sm" className='me-2'> Loading... </Spinner>}
                                                        Sign Up
                                                    </Button>
                                                </div>

                                                <div className="mt-4 text-center">
                                                    <div className="signin-other-title">
                                                        <h5 className="fs-13 mb-4 title text-muted">Create account with</h5>
                                                    </div>

                                                    <div>
                                                        <SocialLoginButtons
                                                            onSocialLogin={(type) => {
                                                                socialResponse(type);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </Form>
                                        </div>
                                    </CardBody>
                                </Card>
                                <div className="mt-4 text-center">
                                    <p className="mb-0">Already have an account ? <Link to="/login" className="fw-semibold text-primary text-decoration-underline"> Signin </Link> </p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        </React.Fragment>
    );
};

export default Register;